import { CommonModule } from "@angular/common";
import { ModuleWithProviders, NgModule, Optional, Renderer2, SkipSelf } from "@angular/core";
import { NbAuthModule, NbPasswordAuthStrategy } from "@nebular/auth";
import { NbRoleProvider, NbSecurityModule } from "@nebular/security";
import { of as observableOf } from "rxjs";
import { APIService } from "./api";
import { GoogleSigninStrategy } from "./auth/google-signin-strategy";
import { JWTStrategy } from "./auth/jwt-strategy";
import { AuthGuard, EnergyGuard, PermissionGuard } from "./guards";
import { httpInterceptorProviders } from "./http-interceptors";
import { throwIfAlreadyLoaded } from "./module-import-guard";
import {
  AnalyticsService,
  AssetHistoryService,
  AssetNotationsService,
  AssetsChangesService,
  AssetsService,
  AssetTypesTreeService,
  AuditExpertService,
  AuditQuestionConfidenceIndicesService,
  AuditsService,
  AutoPpaService,
  BuildingsService,
  ConfigService,
  ConsistencyService,
  ControlPointsStatsService,
  DocumentsService,
  DocumentTypesService,
  ErrorService,
  ExportsService,
  GammesService,
  I18nService,
  ImportsService,
  InitiativeTagsService,
  InsightFiltersService,
  InvestmentCategoriesService,
  InvestmentChartOptionsService,
  InvestmentExecutionsService,
  InvestmentHistoryService,
  InvestmentNotationsService,
  InvestmentReasonsService,
  InvestmentScenariiService,
  InvestmentsChangesService,
  InvestmentSlicesService,
  InvestmentsService,
  InvestmentStatusesService,
  LayoutService,
  NomenclatureService,
  NotationsService,
  PerimetersService,
  PerimeterStatsService,
  PostponementsService,
  PriceSheetService,
  ProjectsService,
  QuantityUnitsService,
  QuickFiltersService,
  ReferenceDataService,
  ReferenceFieldService,
  ReportsService,
  RoadmapNotationsService,
  RoadmapService,
  ScopeService,
  SettingsMenusService,
  SiteService,
  SpiderChartConfigurationService,
  SpreadsheetColumnsAssetsService,
  SpreadsheetColumnsAutoPpaService,
  SpreadsheetColumnsInitiativesService,
  SpreadsheetColumnsInvestmentsService,
  SpreadsheetColumnsService,
  SpreadsheetDialogManagerService,
  SpreadsheetMenuService,
  SpreadsheetSitesService,
  StateLevelsService,
  StyleService,
  TasksService,
  UsersService,
  UtilsService,
  YearsService,
  WorkflowService,
  PerimeterEnergyTrajectoryService,
  RefrigerantTypeService,
  FidjiActService,
  QuotePartService,
  InvestmentWizardService,
  MaintenanceService,
  ComplianceService,
  GroupService
} from "./utils";
import { LanguageService } from "./utils/language.service";
import { NbIconModule } from "@nebular/theme";
import { NbEvaIconsModule } from "@nebular/eva-icons";
import { MicrosoftSigninStrategy, MSALGuardConfigFactory, MSALInstanceFactory } from "./auth/microsoft-sigin-strategy";
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MsalService } from "@azure/msal-angular";

export class NbSimpleRoleProvider extends NbRoleProvider {
  getRole() {
    // here you could provide any role based on any auth flow
    return observableOf("guest");
  }
}

export const NB_CORE_PROVIDERS = [
  ...NbAuthModule.forRoot({
    strategies: [
      // Password
      NbPasswordAuthStrategy.setup({
        name: "email",

        token: {
          key: "token"
        },

        /**
         * Careful: this is statically binded at build time.
         * So we need to dynamically set it in `auth.component.ts`.
         */
        baseEndpoint: null,
        login: {
          endpoint: "/users/api/login/"
        },
        logout: {
          endpoint: "/users/api/logout/"
        },

        errors: {
          key: "message"
        }
      }),
      // Google Signin
      GoogleSigninStrategy.setup({
        name: "google-signin",
        /**
         * Careful: this is statically binded at build time.
         * So we need to dynamically set it in `auth.component.ts`.
         */
        clientId: null
      }),
      // Microsoft sign in
      MicrosoftSigninStrategy.setup({
        name: "microsoft-signin"
      }),
      // JWT
      JWTStrategy.setup({
        name: "jwt"
      })
    ],
    forms: {
      login: {
        showMessages: {
          success: false,
          error: true
        }
      }
    }
  }).providers,

  NbSecurityModule.forRoot({
    accessControl: {
      guest: {
        view: "*"
      },
      user: {
        parent: "guest",
        create: "*",
        edit: "*",
        remove: "*"
      }
    }
  }).providers,

  {
    provide: NbRoleProvider,
    useClass: NbSimpleRoleProvider
  },

  // api
  APIService,

  // auth
  GoogleSigninStrategy,
  JWTStrategy,
  MicrosoftSigninStrategy,
  MsalService,
  {
    provide: MSAL_INSTANCE,
    useFactory: MSALInstanceFactory
  },
  {
    provide: MSAL_GUARD_CONFIG,
    useFactory: MSALGuardConfigFactory
  },

  // guards
  AuthGuard,
  EnergyGuard,
  PermissionGuard,

  // http-interceptors
  httpInterceptorProviders,

  Renderer2,

  // utils
  AssetHistoryService,
  AssetNotationsService,
  AssetsService,
  AssetsChangesService,
  AssetTypesTreeService,
  AnalyticsService,
  AuditQuestionConfidenceIndicesService,
  AuditExpertService,
  AuditsService,
  AutoPpaService,
  BuildingsService,
  ConfigService,
  ConsistencyService,
  ControlPointsStatsService,
  DocumentsService,
  ErrorService,
  ExportsService,
  FidjiActService,
  GammesService,
  I18nService,
  ImportsService,
  InitiativeTagsService,
  InsightFiltersService,
  InvestmentsService,
  InvestmentCategoriesService,
  InvestmentChartOptionsService,
  InvestmentsChangesService,
  InvestmentExecutionsService,
  InvestmentHistoryService,
  InvestmentNotationsService,
  InvestmentReasonsService,
  InvestmentScenariiService,
  InvestmentSlicesService,
  InvestmentStatusesService,
  LanguageService,
  LayoutService,
  NomenclatureService,
  NotationsService,
  DocumentTypesService,
  PerimeterEnergyTrajectoryService,
  PerimetersService,
  PerimeterStatsService,
  PostponementsService,
  PriceSheetService,
  ProjectsService,
  QuantityUnitsService,
  QuickFiltersService,
  ReferenceDataService,
  ReferenceFieldService,
  RefrigerantTypeService,
  ReportsService,
  RoadmapService,
  RoadmapNotationsService,
  ScopeService,
  SettingsMenusService,
  SiteService,
  SpiderChartConfigurationService,
  SpreadsheetColumnsAssetsService,
  SpreadsheetColumnsAutoPpaService,
  SpreadsheetColumnsInitiativesService,
  SpreadsheetColumnsInvestmentsService,
  SpreadsheetColumnsService,
  SpreadsheetDialogManagerService,
  SpreadsheetMenuService,
  SpreadsheetSitesService,
  StateLevelsService,
  StyleService,
  TasksService,
  UsersService,
  UtilsService,
  YearsService,
  WorkflowService,
  QuotePartService,
  InvestmentWizardService,
  MaintenanceService,
  ComplianceService,
  GroupService
];

@NgModule({
  imports: [CommonModule, NbEvaIconsModule, NbIconModule],
  exports: [NbAuthModule],
  declarations: []
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    throwIfAlreadyLoaded(parentModule, "CoreModule");
  }

  static forRoot(): ModuleWithProviders<CoreModule> {
    return <ModuleWithProviders<CoreModule>>{
      ngModule: CoreModule,
      providers: [...NB_CORE_PROVIDERS]
    };
  }
}
